import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useAsync } from 'react-use';
import { Redirect, useLocation } from 'react-router-dom';
import { MdContactPhone } from 'react-icons/md';

import { __t } from '@mssgme/shared';
import { MapPinIcon, CreditCardIcon, ShoppingBagIcon } from '@mssgme/icons';
import { NotFoundError } from '@mssgme/api-client';
import { Spinner } from '@mssgme/ui';
import { useCoreContext, useCRMData, useFrontAPI } from '../../hooks';
import { Step, StepSwitch, ThemedModal } from '../../UI';
import AdjustingStep from './Steps/AdjustingStep';
import ShippingAddressStep from './Steps/ShippingAddressStep';
import PaymentStep from './Steps/PaymentStep';
import ContactInfoStep from './Steps/ContactInfoStep';

import styles from './OrderPage.scss';

OrderPage.propTypes = {
    orderId: PropTypes.string.isRequired,
};

export function OrderPage({ orderId }) {
    const api = useFrontAPI();
    const location = useLocation();
    const { form } = useCRMData();
    const { loading, error, value } = useAsync(() => api.orders.show({ orderId, query: { complete: true } }), [
        orderId,
    ]);
    const { router } = useCoreContext();
    const backPath = location.state?.backPath || router.toHome();

    if (!value) {
        return null;
    }

    if (value.finalized) {
        return <Redirect to={router.orders.toStatus(orderId, value.status)} />;
    }

    return (
        <ThemedModal overlay compact title={__t('checkout')} onClose={backPath}>
            <div className={styles.root}>
                {loading ? (
                    <Spinner />
                ) : error ? (
                    <div className={styles.error}>
                        {NotFoundError.is(error) ? __t('order_not_found') : error.message}
                    </div>
                ) : (
                    <StepSwitch>
                        <Step name={__t('order_adjusting')} icon={ShoppingBagIcon}>
                            <AdjustingStep />
                        </Step>
                        <Step name={__t('order_contact_info')} icon={MdContactPhone}>
                            <ContactInfoStep />
                        </Step>
                        {!!form.collectAddress && (
                            <Step name={__t('order_shipping_address')} icon={MapPinIcon}>
                                <ShippingAddressStep />
                            </Step>
                        )}
                        <Step name={__t('order_payment')} icon={CreditCardIcon}>
                            <PaymentStep />
                        </Step>
                    </StepSwitch>
                )}
            </div>
        </ThemedModal>
    );
}
